import { useMemo } from 'react';

import type { NodeData, RelData } from '../graph-viz-context';
import { useGraphVizContext } from '../graph-viz-context';
import { GraphVizSidepanel } from '../graph-viz-sidepanel';
import { ClipboardCopier } from './clipboard-copier';
import { GraphChip } from './graph-chip';
import { PropertiesTableNvl } from './properties-table';

type DetailPanelProps = {
  paneWidth?: number;
};

type SelectedItem = { dataType: 'node'; data: NodeData } | { dataType: 'relationship'; data: RelData };
export function DetailsPanel({ paneWidth = 400 }: DetailPanelProps) {
  const { selected, nvlGraph } = useGraphVizContext();

  const nodeData = useMemo(() => {
    const [nodeId] = selected.nodeIds;
    if (nodeId !== undefined) {
      return nvlGraph.dataLookupTable.nodes[nodeId];
    }

    return undefined;
  }, [selected, nvlGraph]);

  const relData = useMemo(() => {
    const [relId] = selected.relationshipIds;
    if (relId !== undefined) {
      return nvlGraph.dataLookupTable.relationships[relId];
    }

    return undefined;
  }, [selected, nvlGraph]);

  const selectedItem = useMemo((): SelectedItem | undefined => {
    if (nodeData) {
      return { dataType: 'node', data: nodeData };
    }

    if (relData) {
      return { dataType: 'relationship', data: relData };
    }

    return undefined;
  }, [nodeData, relData]);

  if (selectedItem === undefined) {
    return null;
  }

  const properties = [
    {
      key: '<id>',
      value: `${selectedItem.data.id}`,
      type: 'String',
    },
    ...Object.keys(selectedItem.data.properties).map((key) => {
      const value = selectedItem.data.properties[key];
      const type = selectedItem.data.propertyTypes[key];
      return { key: key, value: value ?? '', type: type ?? '' };
    }),
  ];

  return (
    <>
      <GraphVizSidepanel.Title>
        <h6 className="mr-auto">{selectedItem.dataType === 'node' ? 'Node details' : 'Relationship details'}</h6>
        <ClipboardCopier
          textToCopy={properties.map((prop) => `${prop.key}: ${prop.value}`).join('\n')}
          title="Copy all properties to clipboard"
          ariaLabel="Copy all properties to clipboard"
          iconButtonSize="small"
        />
      </GraphVizSidepanel.Title>
      <GraphVizSidepanel.Content>
        <div className="mx-4 flex flex-row flex-wrap gap-2">
          {selectedItem.dataType === 'node' ? (
            selectedItem.data.labelsSorted.map((label) => (
              <GraphChip
                type="node"
                color={nvlGraph.dataLookupTable.labelMetaData[label]?.mostCommonColor ?? ''}
                label={label}
                key={`node ${label}`}
              />
            ))
          ) : (
            <GraphChip
              type="relationship"
              color={selectedItem.data.color}
              label={selectedItem.data.type}
              key={`relationship ${selectedItem.data.type}`}
            />
          )}
        </div>
        {/* Divider */}
        <div className="bg-palette-neutral-border-weak my-3 h-px w-full" />
        <PropertiesTableNvl propertiesWithTypes={properties} paneWidth={paneWidth} />
      </GraphVizSidepanel.Content>
    </>
  );
}
